/**
 * What kind of dossier this is, and the conventions that follow from it.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * **To add an option, add a row to the table it belongs to and nothing else.**
 * ─────────────────────────────────────────────────────────────────────────
 *
 * These are the facts a financial or sustainability report states about
 * itself, and which every page then has to be consistent with: which
 * framework it is prepared under, what the figures are expressed in, and
 * whether anybody has assured it. They are settings rather than text somebody
 * types on the cover, because each of them is printed in more than one place
 * and the places must agree.
 *
 * A report can hold both kinds at once, which is the ordinary case now: a
 * group publishes its accounts under IFRS and its sustainability statement
 * under ESRS in the same volume. So the accounting framework and the
 * sustainability framework are two settings, not one list.
 *
 * Pure. Names and conventions, no rendering.
 */

/**
 * The accounting framework the statements are prepared under.
 *
 * The list is what is actually used by the people this is for: the two
 * international sets, the French plan, and the Algerian, Moroccan and Tunisian
 * national systems.
 */
export const FRAMEWORKS = {
  none: null,
  ifrs: 'IFRS',
  ifrsSme: 'IFRS for SMEs',
  pcg: 'PCG',
  scf: 'SCF',
  cgnc: 'CGNC',
  ipsas: 'IPSAS',
} as const

/**
 * The sustainability framework, where there is one.
 *
 * Separate from the accounting framework because a report is routinely
 * prepared under one of each, and putting them in one list would make that
 * impossible to say.
 */
export const ESG_FRAMEWORKS = {
  none: null,
  esrs: 'CSRD / ESRS',
  gri: 'GRI',
  tcfd: 'TCFD',
  issb: 'ISSB (IFRS S1 / S2)',
  integrated: '<IR>',
} as const

/**
 * What, if anything, an auditor has said about it.
 *
 * The two levels the CSRD actually distinguishes, plus the case of a report
 * nobody has assured, which has to be sayable rather than left blank and
 * ambiguous.
 */
export const ASSURANCES = {
  none: null,
  limited: 'limited',
  reasonable: 'reasonable',
} as const

/**
 * The currency the figures are in.
 *
 * The symbol is what goes in the unit line under a table. Held here rather
 * than looked up from the locale, because the currency is a fact about the
 * document and the locale is a fact about the reader: a French auditor
 * reading a Tunisian subsidiary's accounts still sees dinars.
 */
export const CURRENCIES = {
  none: { symbol: '' },
  eur: { symbol: '€' },
  usd: { symbol: '$' },
  gbp: { symbol: '£' },
  chf: { symbol: 'CHF' },
  dzd: { symbol: 'DA' },
  mad: { symbol: 'MAD' },
  tnd: { symbol: 'TND' },
} as const

/**
 * The scale the figures are expressed at.
 *
 * The multiplier is not applied to anything: it names the scale so the line
 * above each table can say it. Rewriting figures to match a scale somebody
 * changed in a settings panel is exactly the sort of quiet edit this
 * application must never make.
 */
export const FIGURE_SCALES = {
  none: null,
  units: 'units',
  thousands: 'thousands',
  millions: 'millions',
} as const

/** How headings are numbered through the document. */
export const HEADING_NUMBERS = {
  /** No numbers. */
  none: null,
  /** 1, 1.1, 1.1.1 */
  decimal: 'decimal',
  /** I, A, 1 */
  legal: 'legal',
} as const

export type Framework = keyof typeof FRAMEWORKS
export type EsgFramework = keyof typeof ESG_FRAMEWORKS
export type Assurance = keyof typeof ASSURANCES
export type Currency = keyof typeof CURRENCIES
export type FigureScale = keyof typeof FIGURE_SCALES
export type HeadingNumber = keyof typeof HEADING_NUMBERS

export const FRAMEWORK_IDS = Object.keys(FRAMEWORKS) as readonly Framework[]
export const ESG_FRAMEWORK_IDS = Object.keys(ESG_FRAMEWORKS) as readonly EsgFramework[]
export const ASSURANCE_IDS = Object.keys(ASSURANCES) as readonly Assurance[]
export const CURRENCY_IDS = Object.keys(CURRENCIES) as readonly Currency[]
export const FIGURE_SCALE_IDS = Object.keys(FIGURE_SCALES) as readonly FigureScale[]
export const HEADING_NUMBER_IDS = Object.keys(HEADING_NUMBERS) as readonly HeadingNumber[]

/** The framework's own name, which is never translated. */
export function frameworkName(framework: Framework): string {
  return FRAMEWORKS[framework] ?? ''
}

export function esgFrameworkName(framework: EsgFramework): string {
  return ESG_FRAMEWORKS[framework] ?? ''
}

export function currencySymbol(currency: Currency): string {
  return CURRENCIES[currency].symbol
}

/**
 * A heading's number at a given position in the document.
 *
 * `counts` is the running count at each level, one-based, so `[2, 3]` is the
 * third heading under the second chapter. Levels below the one being numbered
 * are not the caller's business and are ignored.
 */
export function headingNumber(style: HeadingNumber, counts: readonly number[]): string {
  if (style === 'none' || counts.length === 0) return ''
  if (style === 'decimal') return counts.join('.')

  // Legal: the depth decides the alphabet, which is what makes I.A.1 read as
  // a hierarchy rather than as three numbers that happen to be adjacent.
  return counts
    .map((count, depth) => {
      if (depth === 0) return roman(count)
      if (depth === 1) return letter(count)
      return String(count)
    })
    .join('.')
}

const ROMAN: readonly (readonly [number, string])[] = [
  [1000, 'M'],
  [900, 'CM'],
  [500, 'D'],
  [400, 'CD'],
  [100, 'C'],
  [90, 'XC'],
  [50, 'L'],
  [40, 'XL'],
  [10, 'X'],
  [9, 'IX'],
  [5, 'V'],
  [4, 'IV'],
  [1, 'I'],
]

function roman(value: number): string {
  if (!Number.isInteger(value) || value < 1) return ''

  let left = value
  let out = ''
  for (const [amount, numeral] of ROMAN) {
    while (left >= amount) {
      out += numeral
      left -= amount
    }
  }
  return out
}

/** A, B, ... Z, AA, AB: the same carry as a spreadsheet column. */
function letter(value: number): string {
  if (!Number.isInteger(value) || value < 1) return ''

  let left = value - 1
  let out = ''
  while (left >= 0) {
    out = String.fromCharCode(65 + (left % 26)) + out
    left = Math.floor(left / 26) - 1
  }
  return out
}
